const AUTOPLAY_MS = 4500;
const SPIN_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const STEP_DESKTOP = 22;
const STEP_MOBILE = 34;
const STEP_PACK = 45;
const FADE_DESKTOP = 48;

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class HeaderCarousel {
  constructor(root) {
    this.root = root;
    this.spinEl = root.querySelector('.rd-pdp-hcar__spin');
    this.items = [...root.querySelectorAll('.rd-pdp-hcar__item')];
    this.live = root.querySelector('[data-rd-hcar-live]');
    this.activeIndex = 0;
    this.spin = 0;
    this.timer = null;
    this.reduced = prefersReducedMotion();
    this.mq = window.matchMedia('(max-width: 767px)');
    this.radius = 0;
    this.wasOrbit = this.isPackOrbit();
    this.animating = false;

    if (this.items.length === 0 || !this.spinEl) return;

    this.root.classList.toggle('is-orbit', this.isPackOrbit());
    this.measure();
    this.layout({ instant: true });
    this.bind();
    if (this.radius) this.root.classList.add('is-ready');
    this.announce();

    requestAnimationFrame(() => {
      this.measure();
      this.layout({ instant: true });
      if (this.radius) this.root.classList.add('is-ready');
    });

    if (!this.reduced) this.startAutoplay();
  }

  step() {
    if (this.isPackOrbit()) return STEP_PACK;
    if (this.mq.matches) return STEP_MOBILE;
    return STEP_DESKTOP;
  }

  fade() {
    if (this.isPackOrbit()) return this.step() * 2.4;
    return FADE_DESKTOP;
  }

  isPackOrbit() {
    if (!this.mq.matches) return false;
    return Boolean(this.packEl());
  }

  packEl() {
    const hero = this.root.closest('.rd-pdp-hero');
    return hero ? hero.querySelector('.rd-pdp-hero__pack') : null;
  }

  measure() {
    const pack = this.packEl();
    const stage = this.root.querySelector('.rd-pdp-hcar__stage');

    if (this.isPackOrbit() && pack) {
      const packRect = pack.getBoundingClientRect();
      const packSize = Math.max(packRect.width, packRect.height);
      if (packSize > 40) {
        this.radius = this.packOrbitRadius(pack, packSize);
        this.alignStageToPack(pack, stage);
        return;
      }
    }

    this.clearStageAlign(stage);

    const box = stage || this.spinEl;
    if (!box) return;
    const rect = box.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const fallback = Math.min(window.innerHeight * 0.7, 460);
    this.radius = ((size > 80 ? size : fallback) / 2) * 1.06;
  }

  clearStageAlign(stage) {
    if (!stage) return;
    stage.style.left = '';
    stage.style.top = '';
    stage.style.transform = '';
  }

  packOrbitRadius(pack, packSize) {
    const gap = this.mq.matches ? 28 : 40;
    let radius = packSize / 2 + gap;
    if (!this.mq.matches) return radius;

    const hcarRect = this.root.getBoundingClientRect();
    const packRect = pack.getBoundingClientRect();
    const cx = packRect.left + packRect.width / 2;
    const leftRoom = cx - hcarRect.left - 20;
    const labelPad = 136;
    return Math.max(packSize / 2 + 16, Math.min(radius, leftRoom - labelPad));
  }

  alignStageToPack(pack, stage) {
    if (!pack || !stage) return;
    const hcarRect = this.root.getBoundingClientRect();
    const packRect = pack.getBoundingClientRect();
    const cx = packRect.left + packRect.width / 2 - hcarRect.left;
    const cy = packRect.top + packRect.height / 2 - hcarRect.top;
    stage.style.left = `${cx}px`;
    stage.style.top = `${cy}px`;
    stage.style.transform = 'translate(-50%, -50%)';
  }

  setSpinTransform() {
    this.spinEl.style.transform = `rotate(${this.spin}deg)`;
  }

  bind() {
    this.items.forEach((item, index) => {
      const hit = item.querySelector('.rd-pdp-hcar__hit');
      if (!hit) return;
      hit.addEventListener('click', () => {
        this.goTo(index);
        this.pauseForInteraction();
      });
    });

    this.root.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.next();
        this.pauseForInteraction();
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        event.preventDefault();
        this.prev();
        this.pauseForInteraction();
      }
    });

    this.onResize = () => {
      if (this.animating) {
        this.pendingResize = true;
        return;
      }
      const orbit = this.isPackOrbit();
      if (orbit !== this.wasOrbit) {
        this.spin = 0;
        this.wasOrbit = orbit;
      }
      this.measure();
      this.layout({ instant: true });
    };

    window.addEventListener('resize', this.onResize);
    if (this.mq.addEventListener) {
      this.mq.addEventListener('change', this.onResize);
    }

    const stage = this.root.querySelector('.rd-pdp-hcar__stage');
    const hero = this.root.closest('.rd-pdp-hero');
    const pack = hero && hero.querySelector('.rd-pdp-hero__pack');
    if (pack && !pack.complete) {
      pack.addEventListener('load', this.onResize, { once: true });
    }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(this.onResize);
    }
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.onResize);
      if (stage) this.resizeObserver.observe(stage);
      if (hero) this.resizeObserver.observe(hero);
      if (pack) this.resizeObserver.observe(pack);
    }
  }

  layout({ instant = false } = {}) {
    const count = this.items.length;
    if (!count || !this.radius) return;

    const orbit = this.isPackOrbit();
    this.root.classList.toggle('is-orbit', orbit);

    const step = this.step();
    const fade = this.fade();
    const outward = orbit ? -1 : 1;

    if (instant) {
      this.spinEl.style.transition = 'none';
    }

    this.setSpinTransform();

    this.items.forEach((item, index) => {
      let offset = index - this.activeIndex;
      if (offset > count / 2) offset -= count;
      if (offset < -count / 2) offset += count;

      const slot = -this.spin + offset * step;
      const dist = Math.abs(offset * step);
      const ring = Math.abs(offset);
      const visible = orbit ? ring <= 2 : dist <= fade;
      const t = orbit
        ? (visible ? 1 - ring * 0.18 : 0)
        : Math.max(0, 1 - dist / fade);
      const isActive = offset === 0;
      const hit = item.querySelector('.rd-pdp-hcar__hit');
      const prevSlot = Number(item.dataset.hcarSlot);
      const jumped = Number.isFinite(prevSlot) && Math.abs(prevSlot - slot) > step * 1.5;

      if (instant || jumped) {
        item.style.transition = 'none';
        if (hit) hit.style.transition = 'none';
      }

      item.dataset.hcarSlot = String(slot);
      item.style.transform = `rotate(${slot}deg) translateX(${outward * this.radius}px)`;
      item.style.opacity = visible ? String(0.55 + t * 0.45) : '0';
      item.style.zIndex = String(Math.round(t * 10));
      item.style.pointerEvents = visible ? 'auto' : 'none';
      item.classList.toggle('is-active', isActive);
      item.classList.toggle('is-near', !isActive && ring === 1);
      item.classList.toggle('is-far', ring === 2);

      if (hit) {
        hit.classList.toggle('is-left', orbit);
        const slide = 'translateY(-50%)';
        hit.style.transform = `${slide} rotate(${-(slot + this.spin)}deg)`;
        hit.setAttribute('aria-current', isActive ? 'true' : 'false');
      }

      if (instant || jumped) {
        requestAnimationFrame(() => {
          item.style.transition = '';
          if (hit) hit.style.transition = '';
          if (instant) this.spinEl.style.transition = `transform 820ms ${SPIN_EASE}`;
        });
      }
    });
  }

  setActive(index, { instant = false, silent = false } = {}) {
    const count = this.items.length;
    if (!count) return;

    this.activeIndex = ((index % count) + count) % count;
    if (!instant && !this.reduced) this.lockAnimation();
    this.layout({ instant });

    if (!silent) this.announce();
  }

  lockAnimation() {
    this.animating = true;
    window.clearTimeout(this.animTimer);
    this.animTimer = window.setTimeout(() => {
      this.animating = false;
      if (this.pendingResize) {
        this.pendingResize = false;
        this.onResize();
      }
    }, 860);
  }

  goTo(index) {
    const count = this.items.length;
    if (!count || index === this.activeIndex) return;

    let diff = index - this.activeIndex;
    if (diff > count / 2) diff -= count;
    if (diff < -count / 2) diff += count;

    this.spin -= diff * this.step();
    this.setActive(index);
  }

  next() {
    const count = this.items.length;
    if (count < 2) return;
    this.spin -= this.step();
    this.setActive(this.activeIndex + 1);
  }

  prev() {
    const count = this.items.length;
    if (count < 2) return;
    this.spin += this.step();
    this.setActive(this.activeIndex - 1);
  }

  announce() {
    const item = this.items[this.activeIndex];
    if (!item || !this.live) return;
    const label = item.dataset.label || '';
    const value = item.dataset.value || '';
    this.live.textContent = value ? `${label}: ${value}` : label;
  }

  startAutoplay() {
    this.stopAutoplay();
    if (this.reduced || this.items.length < 2) return;
    this.timer = window.setInterval(() => {
      this.next();
    }, AUTOPLAY_MS);
  }

  stopAutoplay() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  pauseForInteraction() {
    this.startAutoplay();
  }
}

export default function initHeaderCarousel() {
  const root = document.querySelector('[data-rd-header-carousel]');
  if (!root) return;
  return new HeaderCarousel(root);
}
